// AuthProvider — context de Supabase Auth + hooks de identidad por rol.
//
// Sprint 2 B8 (cierre): este archivo es ahora el ÚNICO gate de auth.
// Reemplaza al PasswordGate piloto-grade. La transición B1-B7 con
// fallbacks a TEMP_KINE_ID / TEMP_PACIENTE_ID terminó — los hooks que
// resuelven identidad devuelven `string | null` y los callers deben
// proteger sus useEffect cuando es null.
//
// ── Modelo de roles ────────────────────────────────────────────────────
// `kines.id` ES `auth.users.id` (FK directa). Cuando hay sesión, el
// userId YA ES el kine_id — no requiere lookup.
//
// `pacientes.id` es UUID generado, distinto a `auth.users.id`. La fila
// del paciente tiene `pacientes.auth_user_id` que apunta a la sesión.
// Para resolver el paciente_id necesitamos lookup.
//
// Sub-decisión: ningún user puede ser ambos roles (IDs disjuntos por
// construcción + RLS 0003 lo asume).
//
// ── userRole en el context ────────────────────────────────────────────
// Cuando hay sesión activa, resolvemos el rol UNA vez (kine vs paciente)
// y lo cacheamos en el context. AuthGate, AuthCallback, Login y los
// hooks de identidad leen del cache → 1 lookup por session change, no
// uno por componente. Estados de userRole:
//   - undefined : status='loading' o lookup en curso
//   - 'kine' / 'paciente' : lookup completado, rol resuelto
//   - null : lookup completado, NO se encontró ni en kines ni en
//            pacientes (huérfano — auth.users existe pero el linkeo
//            de dominio nunca completó; AuthCallback maneja este caso)

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import { resolveUserRole, type UserRole } from "@/lib/auth/resolveHomePath";
import { isDemoMode } from "@/lib/demo/demoMode";
import { captureSession } from "@/lib/demo/sessionSnapshots";

type AuthStatus = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  /** `auth.users.id` del usuario logueado, o null si no hay sesión. */
  userId: string | null;
  /** Rol resuelto. undefined mientras se carga / lookup en curso.
   *  null = huérfano (auth.users sin row en kines ni pacientes). */
  userRole: UserRole | null | undefined;
  /** ID de la row de dominio. Para kine = userId. Para paciente =
   *  resolved via lookup pacientes.auth_user_id. undefined mientras
   *  carga, null si huérfano o no aplica. */
  pacienteId: string | null | undefined;
  /** Wrap de `supabase.auth.signOut()` — útil para que componentes UI
   *  no importen el cliente Supabase directamente. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null | undefined>(
    undefined,
  );
  const [pacienteId, setPacienteId] = useState<string | null | undefined>(
    undefined,
  );
  // Trackea el userId del lookup en curso para evitar setear estado del
  // lookup viejo si la sesión cambió mid-lookup (logout + login rápido).
  const lookupForUserId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setIsLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (cancelled) return;
      setSession(newSession);
    });

    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  // Lookup del rol cada vez que cambia la sesión. Cachea en context.
  useEffect(() => {
    const userId = session?.user.id ?? null;

    if (!userId) {
      setUserRole(undefined);
      setPacienteId(undefined);
      lookupForUserId.current = null;
      return;
    }

    // Si ya hicimos lookup para este userId, no repetir.
    if (lookupForUserId.current === userId) return;
    lookupForUserId.current = userId;

    setUserRole(undefined);
    setPacienteId(undefined);

    let cancelled = false;
    (async () => {
      const resolved = await resolveUserRole(userId);
      if (cancelled) return;
      // Si la sesión cambió mientras estábamos en el lookup, no setees
      // estado del lookup viejo.
      if (lookupForUserId.current !== userId) return;

      if (!resolved) {
        // Huérfano: AuthCallback maneja este caso.
        setUserRole(null);
        setPacienteId(null);
        return;
      }

      setUserRole(resolved.role);

      // Demo mode: auto-capture del session bajo el rol resuelto. El
      // toggle demo del RoleToggle usa el snapshot para hacer swap real
      // (signOut + setSession) sin passwords en el bundle. Ver
      // docs/decisions/demo-mode.md.
      if (isDemoMode() && session && (resolved.role === "kine" || resolved.role === "paciente")) {
        captureSession(resolved.role, session);
      }

      if (resolved.role === "paciente") {
        // Lookup adicional para resolver pacientes.id (distinto a auth_user_id).
        const { data, error } = await supabase
          .from("pacientes")
          .select("id")
          .eq("auth_user_id", userId)
          .maybeSingle();
        if (cancelled) return;
        if (lookupForUserId.current !== userId) return;
        if (error) {
          console.warn("[AuthProvider] paciente.id lookup error", error);
          setPacienteId(null);
          return;
        }
        setPacienteId((data as { id: string } | null)?.id ?? null);
      } else {
        // Kine: no aplica pacienteId.
        setPacienteId(null);
      }
    })();

    return () => {
      cancelled = true;
      // BUG FIX (B8 hotfix): resetear el ref al cancelar lookup.
      // Sin esto, StrictMode dev rompe el flow así:
      //   Mount #1: lookupForUserId.current = userId, async arranca.
      //   Unmount #1: cleanup setea cancelled=true (PERO no resetea ref).
      //   Mount #2: ve lookupForUserId.current === userId → early return,
      //              NO arranca nuevo lookup.
      //   Async #1 termina pero cancelled=true → no actualiza state.
      //   → userRole queda undefined PARA SIEMPRE.
      // Resultado en UI: hooks que consumen useCurrentKineId/PacienteId
      // hacen early return → todas las queries quedan en loading inicial
      // → header con "—", campos vacíos, dashboard no carga data.
      // Aunque StrictMode solo aplica en dev, el patrón es defectuoso.
      if (lookupForUserId.current === userId) {
        lookupForUserId.current = null;
      }
    };
  }, [session]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // setSession queda a cargo del listener onAuthStateChange.
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: isLoading ? "loading" : session ? "authenticated" : "anonymous",
      session,
      userId: session?.user.id ?? null,
      userRole,
      pacienteId,
      signOut,
    }),
    [isLoading, session, userRole, pacienteId, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Acceso al estado de auth global. Throw si se usa fuera del provider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth() debe usarse dentro de <AuthProvider>");
  }
  return ctx;
}

/** Resuelve `kine_id` para queries. `kines.id` ES `auth.users.id`,
 *  no hay lookup — devuelve `userId` directo si el rol resolvió como
 *  kine. Devuelve null durante loading / lookup-pending / si el user
 *  no es kine. Los callers deben hacer early return cuando es null. */
export function useCurrentKineId(): string | null {
  const { userId, userRole } = useAuth();
  if (userRole !== "kine") return null;
  return userId;
}

/** Resuelve `pacientes.id` para queries. Devuelve el id de la fila
 *  cuando el rol resolvió como paciente y el lookup completó. null
 *  durante loading / lookup-pending / si el user no es paciente. */
export function useCurrentPacienteId(): string | null {
  const { pacienteId, userRole } = useAuth();
  if (userRole !== "paciente") return null;
  return pacienteId ?? null;
}
